import type { CalendarDowntime } from "./downtimes";
import type { SitePosition } from "../../planning/Project";
import { CalendarContext, CalendarElementsProps } from "../../shared/VerticalCalendar";
import { Fragment } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { PossibleCalendarTypes } from "../MainCalendar";
import dayjs from "dayjs";
import ApiService from "@/api/ApiService";
import { useQueries, UseQueryOptions } from "@tanstack/react-query";
import ProjectLine from "./ProjectLine";
import ResourceLine from "./ResourceLine";
import { usePersistantStore } from "@/stores/global";
import MaterialLine from "./MaterialLine";
import PartDateSelector from "../dialogs/PartDateSelector";
import HolidayCrewsLine from "./HolidayCrewsLine";
import BauleiterLine from "./BauleiterLine";
import { mergeBauleiterRows, type BauleiterCalendarRow } from "./bauleiterPeriods";
import useRights from "@/shared/api/useRights";

const sortFunctions = {
  BAULEITER: (a: { id: number; name: string }, b: { id: number; name: string }) => a.name.localeCompare(b.name) || a.id - b.id,
  PROJECTS: (a, b) => (a.id === 0 ? "     0" : (a.number ?? "")).localeCompare(b.id === 0 ? "     0" : (b.number ?? "")),
  CREWS: (a, b) => (a.id === 0 ? "     0" : (a.name ?? "")).localeCompare(b.id === 0 ? "     0" : (b.name ?? "")),
  HOLIDAYS: (a, b) =>
    (a.type === "HOLIDAY_CREWS" ? a.name : a.belongs_to === b.belongs_to ? a.name : a.belongs_to).localeCompare(
      b.type === "HOLIDAY_CREWS" ? b.name : a.belongs_to === b.belongs_to ? b.name : b.belongs_to,
    ) || a.type.localeCompare(b.type),
  STAFF: (a, b) => (a.id === 0 ? "     0" : (a.name ?? "")).localeCompare(b.id === 0 ? "     0" : (b.name ?? "")),
  RESOURCES: (a, b) => {
    const aName = a.id === 0 ? "     0" : (a.name ?? "");
    const bName = b.id === 0 ? "     0" : (b.name ?? "");
    const aHasMiet = aName.toLowerCase().includes("miet");
    const bHasMiet = bName.toLowerCase().includes("miet");
    if (aHasMiet && !bHasMiet) return 1;
    if (!aHasMiet && bHasMiet) return -1;
    return aName.localeCompare(bName);
  },
};

export interface CalProject {
  id: number;
  name: string;
  number: string;
  comment: string;
  position?: SitePosition | null;
}

export interface CalPhase {
  id: number;
  name: string;
  comment: string;
  position?: SitePosition | null;
  project: CalProject;
  downtimes?: CalendarDowntime[];
}

export interface CalJob {
  id: number;
  date: string;
  status: string;
  comment: string;
  position?: SitePosition | null;
  phase: CalPhase;
}

export type CalDataLine<Reference = {}, JobExtension = {}> = Reference & {
  id: number;
  type?: string;
  first: string | null;
  last: string | null;
  name: string;
  max: number;
  autoBookings?: Record<string, unknown>;
  elements: { [date: string]: (CalJob & JobExtension)[] };
};

type CalendarLine = CalDataLine | BauleiterCalendarRow;

interface CalDataResponse {
  [lineId: string]: CalendarLine;
}

function getMonthsInRange(start: dayjs.Dayjs, end: dayjs.Dayjs, months: Set<string> = new Set()) {
  // Base case: if start is after end, return the months array
  if (start.isAfter(end, "month")) {
    return months;
  }

  // Add the current month to the array
  months.add(start.format("YYYY-MM"));

  // Recursively call the function with the next month
  return getMonthsInRange(start.add(1, "month"), end, months);
}

function getWeeksInRange(start: dayjs.Dayjs, end: dayjs.Dayjs, months: Set<string> = new Set()) {
  // Base case: if start is after end, return the months array
  if (start.isAfter(end, "week")) {
    return months;
  }

  // Add the current month to the array
  months.add(start.format("YYYY-MM-DD"));

  // Recursively call the function with the next month
  return getWeeksInRange(start.add(1, "week"), end, months);
}

const OffsetCounter = (init: number) => {
  const ctr = {
    count: 0,
    init: function (val: number) {
      this.count = val;
      return this;
    },
    increment: function (val: number) {
      const current = this.count;
      this.count += val;
      return current;
    },
  };
  return ctr.init(init);
};

export default function InnerCalendar({
  type,
  reference,
  settings,
  setJobInfoId,
  setPositionEditorId,
  pdfref,
  readOnly,
}: {
  type: PossibleCalendarTypes;
  reference: number | null;
}) {
  const region = usePersistantStore((store) => store.region);
  const userId = usePersistantStore((store) => store.userId);
  const rights = useRights();
  const canViewBauleiter = ("OBL" in rights || "DISPOSITION" in rights) && userId > 0;
  const view = useContext(CalendarContext);
  const [hoverLine, setHoverLine] = useState(null);
  const [moveMap, setMoveMap] = useState({});
  const [moveData, setMoveData] = useState(null);

  if (!view) return null;

  const offsetCounter = OffsetCounter(view.offset + 4);

  /* const [months, setMonths] = useState<Set<string>>(new Set());

  useEffect(() => {
    setMonths(
      (c) => new Set([...c, ...getMonthsInRange(view.calStart, view.calEnd)])
    );
  }, [view.calStart, view.calEnd]);
  */

  /* const months = useMemo(
    () => getMonthsInRange(view.safeStart, view.safeEnd),
    [view.safeStart, view.safeEnd]
  ); */

  const weeks = useMemo(() => getWeeksInRange(view.safeStart.startOf("week"), view.safeEnd), [view.safeStart, view.safeEnd]);

  const data = useQueries<UseQueryOptions<CalDataResponse>[], CalendarLine[]>({
    queries: Array.from(weeks).map((month) => {
      return {
        queryKey: ["getCalendar", type, "" + reference, month, region, ...(type === "BAULEITER" ? [userId, "project-periods"] : [])],
        queryFn: ApiService.getCalendar(type, reference ?? 0, month, region),
        enabled: type !== "BAULEITER" || canViewBauleiter,
      };
    }),
    combine: (results) =>
      Object.values(
        results
          .filter((r) => r.isSuccess)
          .reduce((carry: CalDataResponse, monthQuery) => {
            for (const lineId in monthQuery.data) {
              const incoming = monthQuery.data[lineId];
              const current = carry[lineId];
              if ("projects" in incoming) {
                carry[lineId] = current && "projects" in current ? mergeBauleiterRows(current, incoming) : incoming;
              } else if (current && !("projects" in current)) {
                carry[lineId] = {
                  ...current,
                  autoBookings: {
                    ...(current.autoBookings ?? {}),
                    ...((incoming.autoBookings && !Array.isArray(incoming.autoBookings) ? incoming.autoBookings : {}) ?? {}),
                  },
                  first: !current.first || (incoming.first !== null && current.first > incoming.first) ? incoming.first : current.first,
                  last: !current.last || (incoming.last !== null && current.last < incoming.last) ? incoming.last : current.last,
                  max: Math.max(current.max, incoming.max),
                  elements: { ...current.elements, ...incoming.elements },
                };
              } else {
                carry[lineId] = incoming;
              }
            }
            return carry;
          }, {} as CalDataResponse),
      ).sort(sortFunctions[type]),
  });

  if (type === "BAULEITER" && !canViewBauleiter) return null;

  return data
    .filter((x) => "projects" in x || !x.first || (view.safeStart.format("YYYY-MM-DD") <= x.last && view.safeEnd.format("YYYY-MM-DD") >= x.first))
    .map((x) =>
      "projects" in x ? (
        <BauleiterLine key={x.id} data={x} offset={offsetCounter.increment(1)} />
      ) : x?.type === "HOLIDAY_CREWS" ? (
        <HolidayCrewsLine key={x.id} data={x} offset={offsetCounter.increment(1)} />
      ) : type === "PROJECTS" ? (
        <ProjectLine key={x.id} setJobInfoId={setJobInfoId} readOnly={readOnly} data={x} offset={offsetCounter.increment(1)} />
      ) : type === "MATERIAL" || type === "DISPO" ? null : (
        <ResourceLine
          type={x?.type === "HOLIDAY_STAFF" ? "STAFF" : x?.type || type}
          partDateSelectorRef={pdfref}
          setJobInfoId={setJobInfoId}
          setPositionEditorId={setPositionEditorId}
          settings={settings}
          hoverLine={hoverLine}
          readOnly={readOnly || (x?.type || type) !== type}
          setHoverLine={setHoverLine}
          moveMap={moveMap}
          setMoveMap={setMoveMap}
          setMoveData={setMoveData}
          moveData={moveData}
          key={x.id}
          data={x}
          offset={offsetCounter.increment(1)}
        />
      ),
    );
}
